export type SoundKey =
  | 'bomb_place' | 'bomb_explode' | 'block_break' | 'powerup_get'
  | 'enemy_die' | 'player_die' | 'level_complete' | 'game_over'
  | 'menu_click' | 'menu_hover' | 'victory';

export class AudioSystem {
  private ctx: AudioContext | null = null;
  private masterGain!: GainNode;
  private musicGain!: GainNode;
  private sfxGain!: GainNode;
  private musicNodes = new Map<AudioScheduledSourceNode, GainNode>();
  private musicTimer: ReturnType<typeof setInterval> | null = null;
  private shaker: AudioBuffer | null = null;
  private _muted = false;
  private _masterVol = 0.8;
  private _musicVol = 0.35;
  private _sfxVol = 0.9;
  private musicPlaying = false;

  private ensureCtx(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.musicGain = this.ctx.createGain();
      this.sfxGain = this.ctx.createGain();
      this.masterGain.connect(this.ctx.destination);
      this.musicGain.connect(this.masterGain);
      this.sfxGain.connect(this.masterGain);
      this.applyVolumes();
    }
    return this.ctx;
  }

  private applyVolumes(): void {
    if (!this.masterGain) return;
    this.masterGain.gain.value = this._muted ? 0 : this._masterVol;
    this.musicGain.gain.value = this._musicVol;
    this.sfxGain.gain.value = this._sfxVol;
  }

  set masterVolume(v: number) { this._masterVol = v; this.applyVolumes(); }
  set musicVolume(v: number) { this._musicVol = v; this.applyVolumes(); }
  set sfxVolume(v: number) { this._sfxVol = v; this.applyVolumes(); }
  get muted(): boolean { return this._muted; }

  toggleMute(): void {
    this._muted = !this._muted;
    this.applyVolumes();
  }

  play(key: SoundKey): void {
    try {
      const ctx = this.ensureCtx();
      if (ctx.state === 'suspended') ctx.resume();
      this.synthesize(ctx, key);
    } catch { /* silent fail */ }
  }

  private synthesize(ctx: AudioContext, key: SoundKey): void {
    const now = ctx.currentTime;
    switch (key) {
      case 'bomb_place': this.tone(ctx, now, [220, 180], [0, 0.08], 0.08, 'sine', 0.3); break;
      case 'bomb_explode': this.noise(ctx, now, 0.6, 0.7, 80); break;
      case 'block_break': this.tone(ctx, now, [300, 150], [0, 0.1], 0.15, 'square', 0.25); break;
      case 'powerup_get': this.arpeggio(ctx, now, [440, 550, 660, 880], 0.06, 0.4); break;
      case 'enemy_die': this.tone(ctx, now, [400, 100], [0, 0.2], 0.25, 'sawtooth', 0.3); break;
      case 'player_die': this.tone(ctx, now, [440, 110], [0, 0.5], 0.6, 'sawtooth', 0.4); break;
      case 'level_complete': this.arpeggio(ctx, now, [523, 659, 784, 1047], 0.1, 0.5); break;
      case 'game_over': this.tone(ctx, now, [220, 55], [0, 0.8], 0.9, 'sawtooth', 0.5); break;
      case 'menu_click': this.tone(ctx, now, [660, 880], [0, 0.05], 0.08, 'sine', 0.2); break;
      case 'menu_hover': this.tone(ctx, now, [440, 440], [0, 0.03], 0.04, 'sine', 0.1); break;
      case 'victory': this.arpeggio(ctx, now, [523, 659, 784, 1047, 1319], 0.08, 0.6); break;
    }
  }

  private tone(
    ctx: AudioContext, now: number, freqs: number[], times: number[],
    dur: number, type: OscillatorType, vol: number
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.connect(gain);
    gain.connect(this.sfxGain);
    for (let i = 0; i < freqs.length; i++) {
      osc.frequency.setValueAtTime(freqs[i], now + times[i]);
    }
    gain.gain.setValueAtTime(vol, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    osc.start(now);
    osc.stop(now + dur);
  }

  private noise(ctx: AudioContext, now: number, dur: number, vol: number, cutoff: number): void {
    const bufSize = ctx.sampleRate * dur;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(vol, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain);
    src.start(now);
  }

  private arpeggio(ctx: AudioContext, now: number, freqs: number[], step: number, vol: number): void {
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      osc.connect(gain);
      gain.connect(this.sfxGain);
      const t = now + i * step;
      gain.gain.setValueAtTime(vol, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + step * 2);
      osc.start(t);
      osc.stop(t + step * 2);
    });
  }

  startMusic(type: 'menu' | 'game' | 'boss'): void {
    this.stopMusic();
    try {
      const ctx = this.ensureCtx();
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
      this.musicPlaying = true;
      this.playBgMusic(ctx, type);
    } catch { /* Audio remains optional. */ }
  }

  private musicNote(ctx: AudioContext, at: number, frequency: number, duration: number,
    volume: number, waveform: OscillatorType = 'sine', attack = 0.015): void {
    const oscillator = ctx.createOscillator(), envelope = ctx.createGain();
    oscillator.type = waveform; oscillator.frequency.value = frequency;
    oscillator.connect(envelope); envelope.connect(this.musicGain);
    envelope.gain.setValueAtTime(0.0001, at);
    envelope.gain.exponentialRampToValueAtTime(volume, at + attack);
    envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    this.musicNodes.set(oscillator, envelope);
    oscillator.onended = () => { this.musicNodes.delete(oscillator); oscillator.disconnect(); envelope.disconnect(); };
    oscillator.start(at); oscillator.stop(at + duration + 0.01);
  }

  private musicShaker(ctx: AudioContext, at: number): void {
    if (!this.shaker) {
      this.shaker = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.055), ctx.sampleRate);
      const data = this.shaker.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 3);
    }
    const source = ctx.createBufferSource(), gain = ctx.createGain();
    const filter = ctx.createBiquadFilter(); filter.type = 'highpass'; filter.frequency.value = 5000;
    source.buffer = this.shaker; gain.gain.value = 0.016;
    source.connect(filter); filter.connect(gain); gain.connect(this.musicGain);
    this.musicNodes.set(source, gain);
    source.onended = () => { this.musicNodes.delete(source); source.disconnect(); filter.disconnect(); gain.disconnect(); };
    source.start(at);
  }

  private playBgMusic(ctx: AudioContext, type: 'menu' | 'game' | 'boss'): void {
    // A quiet eight-bar tropical groove: Cmaj7 / Am7 / Fmaj7 / G6.
    const bpm = type === 'boss' ? 116 : type === 'menu' ? 88 : 100;
    const stepDuration = 60 / bpm / 2;
    const chords = [[261.63, 329.63, 392], [220, 261.63, 329.63], [174.61, 220, 261.63], [196, 246.94, 329.63]];
    const bass = [130.81, 110, 87.31, 98];
    const melody = [523.25, 0, 659.25, 783.99, 659.25, 0, 587.33, 0,
      659.25, 0, 523.25, 440, 523.25, 0, 659.25, 0,
      523.25, 587.33, 659.25, 0, 783.99, 0, 659.25, 587.33,
      587.33, 0, 659.25, 783.99, 659.25, 587.33, 523.25, 0];
    let step = 0, nextAt = ctx.currentTime + 0.06;
    const schedule = () => {
      if (!this.musicPlaying || ctx.state !== 'running') return;
      if (nextAt < ctx.currentTime - 0.25) nextAt = ctx.currentTime + 0.04;
      while (nextAt < ctx.currentTime + 0.15) {
        const chord = Math.floor(step / 16) % chords.length;
        const note = melody[step % melody.length];
        if (note) {
          this.musicNote(ctx, nextAt, note, 0.4, 0.045);
          this.musicNote(ctx, nextAt, note * 2.01, 0.15, 0.007);
        }
        if (step % 8 === 0) for (const frequency of chords[chord])
          this.musicNote(ctx, nextAt, frequency, stepDuration * 6, 0.012, 'triangle', 0.1);
        if (step % 4 === 0) this.musicNote(ctx, nextAt, bass[chord], stepDuration * 2.8, 0.055, 'sine', 0.025);
        if (step % 2 === 1) this.musicShaker(ctx, nextAt);
        step++; nextAt += stepDuration;
      }
    };
    schedule(); this.musicTimer = setInterval(schedule, 50);
  }

  stopMusic(): void {
    this.musicPlaying = false;
    if (this.musicTimer !== null) clearInterval(this.musicTimer);
    this.musicTimer = null;
    const now = this.ctx?.currentTime ?? 0;
    for (const [source, gain] of this.musicNodes) {
      try {
        gain.gain.cancelScheduledValues(now); gain.gain.setTargetAtTime(0.0001, now, 0.012);
        source.stop(now + 0.05);
      } catch { /* An ended voice is already silent. */ }
    }
    this.musicNodes.clear();
  }

  resume(): void {
    void this.ctx?.resume().catch(() => {});
  }
}

export const audioSystem = new AudioSystem();
